import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDateTime, formatMetricValue, describeMetric } from '../lib/format'
import type { Alert, Customer, Device, DeviceMetrics } from '../lib/types'
import { LogoIcon } from '../components/Icons'
import { ThemeToggle } from '../components/ThemeToggle'
import { DeviceStateBadge, StatusBadge } from '../components/Status'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { Monatsbericht } from '../components/Monatsbericht'
import { Nis2Nachweis } from '../components/Nis2Nachweis'

type Reiter = 'uebersicht' | 'geraete' | 'alarme' | 'berichte'

const REITER: [Reiter, string][] = [
  ['uebersicht', 'Übersicht'],
  ['geraete', 'Geräte'],
  ['alarme', 'Alarme'],
  ['berichte', 'Berichte'],
]

/** Zählt die Geräte nach ihrem Zustand für die Kacheln der Übersicht. */
export function zustaende(devices: Device[]): { ok: number; warn: number; crit: number; unbekannt: number } {
  const out = { ok: 0, warn: 0, crit: 0, unbekannt: 0 }
  for (const d of devices) {
    if (d.state === 'ok' || d.state === 'warn' || d.state === 'crit') out[d.state] += 1
    else out.unbekannt += 1
  }
  return out
}

/**
 * Kundenportal (Stufe 6.9): was eine Person beim Kunden über ihr Netz sehen
 * darf – Zustand der Geräte, Alarme, Monatsbericht und NIS2-Nachweis. Nur
 * lesen; kein Fernzugriff, keine Konfigurationen, keine Einstellungen. Die
 * Zentrale lässt ein Portalkonto ohnehin nur an diese Daten (portal.go); die
 * Seite zeigt deshalb gar nicht erst mehr an.
 */
export function KundenportalPage() {
  const { me, logout } = useAuth()
  const [reiter, setReiter] = useState<Reiter>('uebersicht')
  const kunden = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
  })
  if (!me) return null
  const kunde = kunden.data?.find((c) => c.id === me.portal_customer_id)

  return (
    <div className="portal">
      <header className="portal-kopf">
        <div className="row" style={{ gap: 10, fontWeight: 700 }}>
          <LogoIcon size={24} />
          <span>Netzwarte</span>
          <span className="muted" style={{ fontWeight: 400 }}>
            · Kundenportal{kunde ? ` · ${kunde.name}` : ''}
          </span>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <span className="muted" style={{ fontSize: 13 }}>
            {me.name}
          </span>
          <ThemeToggle />
          <button type="button" className="btn btn-sm" onClick={logout}>
            Abmelden
          </button>
        </div>
      </header>
      <main className="portal-inhalt stack" style={{ gap: 16 }}>
        <div role="tablist" aria-label="Bereich" className="reiterleiste">
          {REITER.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={reiter === key}
              className="reiter"
              onClick={() => setReiter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        {kunden.isError ? (
          <ErrorBox error={kunden.error} what="Die Kundendaten" />
        ) : kunden.isPending ? (
          <Loading what="Kundendaten" />
        ) : !kunde ? (
          <EmptyState title="Kein Zugriff" hint="Dieses Konto ist keinem aktiven Kunden zugeordnet." />
        ) : reiter === 'uebersicht' ? (
          <Uebersicht />
        ) : reiter === 'geraete' ? (
          <Geraete />
        ) : reiter === 'alarme' ? (
          <Alarme />
        ) : (
          <div className="stack" style={{ gap: 16 }}>
            <Monatsbericht customer={kunde} />
            <Nis2Nachweis customer={kunde} />
          </div>
        )}
      </main>
    </div>
  )
}

function useGeraete() {
  return useQuery({
    queryKey: ['portal-geraete'],
    queryFn: ({ signal }) => apiGet<Device[]>('/devices', signal),
    refetchInterval: 60_000,
  })
}

function useAlarme() {
  return useQuery({
    queryKey: ['portal-alarme'],
    queryFn: ({ signal }) => apiGet<Alert[]>('/alerts?alle=1&anzahl=200', signal),
    refetchInterval: 60_000,
  })
}

function Uebersicht() {
  const geraete = useGeraete()
  const alarme = useAlarme()
  if (geraete.isError) return <ErrorBox error={geraete.error} what="Die Geräte" />
  if (alarme.isError) return <ErrorBox error={alarme.error} what="Die Alarme" />
  if (geraete.isPending || alarme.isPending) return <Loading what="Übersicht" />
  const z = zustaende(geraete.data)
  const offen = alarme.data.filter((a) => !a.closed_at)
  return (
    <>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Kachel titel="Geräte" wert={geraete.data.length} />
        <Kachel titel="Erreichbar" wert={z.ok} />
        <Kachel titel="Mit Warnung" wert={z.warn} />
        <Kachel titel="Nicht erreichbar" wert={z.crit} />
        <Kachel titel="Offene Alarme" wert={offen.length} />
      </div>
      <AlarmTabelle alarme={offen} titel="Offene Alarme" leer="Gerade ist alles in Ordnung." />
    </>
  )
}

function Kachel({ titel, wert }: { titel: string; wert: number }) {
  return (
    <section className="card" style={{ minWidth: 150, padding: '12px 16px' }}>
      <div className="muted" style={{ fontSize: 13 }}>
        {titel}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700 }}>{wert}</div>
    </section>
  )
}

function Geraete() {
  const geraete = useGeraete()
  const [auswahl, setAuswahl] = useState<string | null>(null)
  if (geraete.isError) return <ErrorBox error={geraete.error} what="Die Geräte" />
  if (geraete.isPending) return <Loading what="Geräte" />
  if (geraete.data.length === 0)
    return <EmptyState title="Noch keine Geräte" hint="Die Geräte richtet der Dienstleister ein." />
  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
      <section className="card grow" style={{ minWidth: 320 }}>
        <table className="data">
          <thead>
            <tr>
              <th>Gerät</th>
              <th>Standort</th>
              <th>Art</th>
              <th>Zustand</th>
            </tr>
          </thead>
          <tbody>
            {geraete.data.map((d) => (
              <tr key={d.id} aria-selected={auswahl === d.id}>
                <td>
                  <button type="button" className="btn-link" onClick={() => setAuswahl(d.id)}>
                    {d.name}
                  </button>
                  <div className="muted mono" style={{ fontSize: 12 }}>
                    {d.mgmt_ip}
                  </div>
                </td>
                <td className="muted">{d.site_name ?? '–'}</td>
                <td className="muted">{[d.vendor, d.model].filter(Boolean).join(' ') || d.type || '–'}</td>
                <td>
                  <DeviceStateBadge state={d.state} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {auswahl ? <GeraetWerte device={geraete.data.find((d) => d.id === auswahl)} /> : null}
    </div>
  )
}

/** Die letzten Werte eines Geräts, ohne Verlauf und ohne Bedienung. */
function GeraetWerte({ device }: { device?: Device }) {
  const werte = useQuery({
    queryKey: ['portal-werte', device?.id],
    queryFn: ({ signal }) => apiGet<DeviceMetrics>(`/devices/${device?.id}/metrics`, signal),
    enabled: Boolean(device),
    refetchInterval: 60_000,
  })
  if (!device) return null
  return (
    <section className="card" style={{ width: 380, maxWidth: '100%' }}>
      <div className="card-head">
        <h2 className="card-title">{device.name}</h2>
      </div>
      <div className="card-body">
        {werte.isError ? (
          <ErrorBox error={werte.error} what="Die Werte" />
        ) : werte.isPending ? (
          <Loading what="Werte" />
        ) : werte.data.latest.length === 0 ? (
          <span className="muted">Noch keine Werte.</span>
        ) : (
          <table className="data">
            <tbody>
              {werte.data.latest
                .filter((s) => !s.labels || Object.keys(s.labels).length === 0)
                .map((s) => (
                  <tr key={s.metric}>
                    <td>{describeMetric(s.metric).label}</td>
                    <td className="mono">{formatMetricValue(s.metric, s.value)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}

function Alarme() {
  const alarme = useAlarme()
  if (alarme.isError) return <ErrorBox error={alarme.error} what="Die Alarme" />
  if (alarme.isPending) return <Loading what="Alarme" />
  return (
    <>
      <AlarmTabelle alarme={alarme.data.filter((a) => !a.closed_at)} titel="Offen" leer="Keine offenen Alarme." />
      <AlarmTabelle
        alarme={alarme.data.filter((a) => a.closed_at)}
        titel="Erledigt"
        leer="Noch keine erledigten Alarme."
      />
    </>
  )
}

function AlarmTabelle({ alarme, titel, leer }: { alarme: Alert[]; titel: string; leer: string }) {
  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{titel}</h2>
      </div>
      {alarme.length === 0 ? (
        <div className="empty">
          <span>{leer}</span>
        </div>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Schwere</th>
              <th>Alarm</th>
              <th>Seit</th>
              <th>Stand</th>
            </tr>
          </thead>
          <tbody>
            {alarme.map((a) => (
              <tr key={a.id}>
                <td>
                  <StatusBadge state={a.severity} label={a.severity === 'crit' ? 'Kritisch' : 'Warnung'} />
                </td>
                <td>
                  {a.title}
                  {a.detail ? (
                    <div className="muted" style={{ fontSize: 12 }}>
                      {a.detail}
                    </div>
                  ) : null}
                </td>
                <td className="mono">{formatDateTime(a.opened_at)}</td>
                <td className="muted">
                  {a.closed_at
                    ? `erledigt ${formatDateTime(a.closed_at)}`
                    : a.acknowledged_at
                      ? 'in Bearbeitung'
                      : 'offen'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
